// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson
// This file is part of Hushold.
//
// Hushold is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as
// published by the Free Software Foundation, version 3.

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../store/AppContext.js';
import { formatAmount, getAccountTransactions } from '../../lib/eventLog.js';
import { calculateCurrencyGainLoss } from '../../lib/currency-math.js';

export function CurrencyGainLoss() {
  const { t } = useTranslation(['reports', 'common']);
  const { state } = useApp();
  const { ledger } = state;
  const homeCurrency = ledger.settings.homeCurrency;

  const today = new Date().toISOString().slice(0, 10);
  const firstOfYear = `${today.slice(0, 4)}-01-01`;

  const [startDate, setStartDate] = useState(firstOfYear);
  const [endDate, setEndDate] = useState(today);

  const allPrices = useMemo(() => [...ledger.prices.values()], [ledger.prices]);

  const foreignAccounts = useMemo(() =>
    [...ledger.accounts.values()].filter(
      (a) => !a.placeholder && !a.hidden && a.currency !== homeCurrency && a.type !== 'TRADING',
    ),
    [ledger.accounts, homeCurrency],
  );

  const rows = useMemo(() => {
    return foreignAccounts.map((account) => {
      const txns = getAccountTransactions(ledger, account.id);
      const result = calculateCurrencyGainLoss(account, txns, allPrices, startDate, endDate, homeCurrency);
      return { account, ...result };
    });
  }, [foreignAccounts, ledger, allPrices, startDate, endDate, homeCurrency]);

  const totalGainLoss = rows.reduce((sum, r) => sum + r.gainLoss, 0);
  const hasAnyRate = rows.some((r) => r.openingValue !== 0 || r.closingValue !== 0);

  return (
    <div className="flex-1 overflow-auto p-6 max-w-4xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-md font-semibold text-text">{t('reports:titles.currencyGainLoss')}</h2>
          <p className="text-xs text-text-muted mt-0.5">
            {t('reports:currencyGainLoss.subtitle', { homeCurrency })}
          </p>
        </div>
      </div>

      {/* Date range */}
      <div className="flex items-center gap-3 mb-6">
        <div>
          <label className="block text-xs text-text-muted mb-1">{t('reports:controls.from')}</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="bg-bg border border-border text-text text-sm px-3 py-1.5 focus:border-accent focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-xs text-text-muted mb-1">{t('reports:controls.to')}</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="bg-bg border border-border text-text text-sm px-3 py-1.5 focus:border-accent focus:outline-none"
          />
        </div>
      </div>

      {foreignAccounts.length === 0 ? (
        <div className="bg-bg-surface border border-border p-8 text-center text-text-muted text-sm">
          {t('reports:currencyGainLoss.noForeignAccounts', { homeCurrency })}
        </div>
      ) : !hasAnyRate ? (
        <div className="bg-bg-surface border border-border p-8 text-center text-text-muted text-sm">
          {t('reports:currencyGainLoss.noRates')}
        </div>
      ) : (
        <div className="bg-bg-surface border border-border">
          <table className="w-full text-sm">
            <thead className="border-b border-border-strong">
              <tr>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:columns.account')}</th>
                <th className="text-left text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:columns.currency')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:sections.openingBalance')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:currencyGainLoss.openingValue', { homeCurrency })}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:sections.closingBalance')}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:currencyGainLoss.closingValue', { homeCurrency })}</th>
                <th className="text-right text-xs font-medium text-text-muted uppercase tracking-wide py-2 px-4">{t('reports:sections.gainLoss')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(({ account, openingBalance, closingBalance, openingValue, closingValue, gainLoss }) => (
                <tr key={account.id} className="border-b border-border hover:bg-bg-elevated">
                  <td className="py-2 px-4 text-text">{account.name}</td>
                  <td className="py-2 px-4 font-mono text-text-muted text-xs">{account.currency}</td>
                  <td className="py-2 px-4 text-right font-mono text-text-muted text-xs">
                    {formatAmount(openingBalance, account.currency)}
                  </td>
                  <td className="py-2 px-4 text-right font-mono text-xs">
                    {openingValue !== 0 ? formatAmount(openingValue, homeCurrency) : <span className="text-text-subtle">—</span>}
                  </td>
                  <td className="py-2 px-4 text-right font-mono text-text-muted text-xs">
                    {formatAmount(closingBalance, account.currency)}
                  </td>
                  <td className="py-2 px-4 text-right font-mono text-xs">
                    {closingValue !== 0 ? formatAmount(closingValue, homeCurrency) : <span className="text-text-subtle">—</span>}
                  </td>
                  <td className={`py-2 px-4 text-right font-mono text-sm font-medium ${gainLoss > 0 ? 'text-positive' : gainLoss < 0 ? 'text-negative' : 'text-text-muted'}`}>
                    {gainLoss > 0 ? '+' : ''}{formatAmount(gainLoss, homeCurrency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-border-strong bg-bg-surface">
              <tr>
                <td colSpan={6} className="py-2 px-4 text-xs font-medium text-text-muted text-right uppercase tracking-wide">
                  {t('reports:currencyGainLoss.totalUnrealized')}
                </td>
                <td className={`py-2 px-4 text-right font-mono text-sm font-semibold ${totalGainLoss > 0 ? 'text-positive' : totalGainLoss < 0 ? 'text-negative' : 'text-text-muted'}`}>
                  {totalGainLoss > 0 ? '+' : ''}{formatAmount(totalGainLoss, homeCurrency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <p className="mt-4 text-xs text-text-subtle">
        * {t('reports:currencyGainLoss.footnote')}
      </p>
    </div>
  );
}
